import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const api = {
  getUnits: (params) => client.get("/units", { params }).then((r) => r.data),
  getStats: () => client.get("/stats").then((r) => r.data),
  getAvailability: () => client.get("/availability").then((r) => r.data),
  getSizes: () => client.get("/sizes").then((r) => r.data.sizes),
  suggestAddress: (q) => client.get("/address/suggest", { params: { q } }).then((r) => r.data.suggestions),
  submitEnquiry: (body) => client.post("/enquiry", body).then((r) => r.data),
  getWaitlist: (params) => client.get("/waitlist", { params }).then((r) => r.data),
  getWaitlistNewCount: () => client.get("/waitlist/new-count").then((r) => r.data),
  acknowledgeWaitlist: (id) => client.post(`/waitlist/${id}/acknowledge`).then((r) => r.data),
  addWaitlistNote: (id, text) => client.post(`/waitlist/${id}/note`, { text }).then((r) => r.data),
  getWaitlistStats: () => client.get("/waitlist/stats").then((r) => r.data),
  updateWaitlist: (id, body) => client.patch(`/waitlist/${id}`, body).then((r) => r.data),
  assignWaitlist: (id, body) => client.post(`/waitlist/${id}/assign`, body).then((r) => r.data),
  deleteWaitlist: (id) => client.delete(`/waitlist/${id}`).then((r) => r.data),
  updateUnit: (id, body) => client.patch(`/units/${id}`, body).then((r) => r.data),
  createUnit: (body) => client.post("/units", body).then((r) => r.data),
  getReadings: (id) => client.get(`/units/${id}/readings`).then((r) => r.data),
  addReading: (id, body) => client.post(`/units/${id}/readings`, body).then((r) => r.data),
  deleteReading: (id, rid) => client.delete(`/units/${id}/readings/${rid}`).then((r) => r.data),
  getBilling: (id) => client.get(`/units/${id}/billing`).then((r) => r.data),
  createInvoice: (id) => client.post(`/units/${id}/invoices`).then((r) => r.data),
  downloadXeroCsv: (invoiceId) => client.get(`/invoices/${invoiceId}/xero-csv`, { responseType: "blob" }).then((r) => r.data),
  uploadMeterPhoto: (unitId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/uploads/meter-photo?unit_id=${unitId}`, fd).then((r) => r.data);
  },
  photoUrl: (path) => `${API}/uploads/${path}`,
  // auth
  me: () => client.get("/auth/me").then((r) => r.data),
  login: (body) => client.post("/auth/login", body).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  refresh: () => client.post("/auth/refresh").then((r) => r.data),
  register: (body) => client.post("/auth/register", body).then((r) => r.data),
  forgotPassword: (body) => client.post("/auth/forgot-password", body).then((r) => r.data),
  resetPassword: (body) => client.post("/auth/reset-password", body).then((r) => r.data),
  // pricing + sites
  getPricing: () => client.get("/pricing").then((r) => r.data),
  getSites: () => client.get("/sites").then((r) => r.data),
  // sigenstor
  getSigenConfig: () => client.get("/sigen/config").then((r) => r.data),
  updateSigenConfig: (body) => client.put("/admin/sigen/config", body).then((r) => r.data),
  getSigenHistory: (days = 30) => client.get(`/sigen/history?days=${days}`).then((r) => r.data),
  getSigenHistorySummary: (days = 30) => client.get(`/sigen/history/summary?days=${days}`).then((r) => r.data),
  // admin
  adminListUsers: () => client.get("/admin/users").then((r) => r.data),
  adminCreateUser: (body) => client.post("/admin/users", body).then((r) => r.data),
  adminUpdateUser: (id, body) => client.put(`/admin/users/${id}`, body).then((r) => r.data),
  adminDeleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  adminAddSite: (body) => client.post("/admin/sites", body).then((r) => r.data),
  adminDeleteSite: (key) => client.delete(`/admin/sites/${key}`).then((r) => r.data),
  adminCreateUnit: (body) => client.post("/admin/units", body).then((r) => r.data),
  adminDeleteUnit: (id) => client.delete(`/admin/units/${id}`).then((r) => r.data),
  adminUpdatePricing: (body) => client.put("/admin/pricing", body).then((r) => r.data),
  // messaging (free email-to-SMS)
  getMessagingConfig: () => client.get("/messaging/config").then((r) => r.data),
  adminUpdateMessaging: (body) => client.put("/admin/messaging", body).then((r) => r.data),
  paymentReminder: (id, confirm) => client.post(`/units/${id}/payment-reminder`, { confirm }).then((r) => r.data),
  reviewRequest: (id, confirm) => client.post(`/units/${id}/review-request`, { confirm }).then((r) => r.data),
  getUnitMessages: (id) => client.get(`/units/${id}/messages`).then((r) => r.data),
  getReplies: () => client.get("/replies").then((r) => r.data),
  getUnreadReplies: () => client.get("/replies/unread-count").then((r) => r.data),
  markReplyHandled: (id) => client.post(`/replies/${id}/handled`).then((r) => r.data),
  // Xero overdue alerts
  getOverdue: () => client.get("/overdue").then((r) => r.data),
  getOverdueSummary: () => client.get("/overdue/summary").then((r) => r.data),
  overdueCheckNow: () => client.post("/overdue/check-now").then((r) => r.data),
  getUnitEvents: (id) => client.get(`/units/${id}/events`).then((r) => r.data),
  addUnitEvent: (id, body) => client.post(`/units/${id}/events`, body).then((r) => r.data),
  lockoutUnit: (id, reason) => client.post(`/units/${id}/lockout`, { reason }).then((r) => r.data),
  restoreUnitAccess: (id) => client.post(`/units/${id}/restore-access`).then((r) => r.data),
  adminDeleteEvent: (id) => client.delete(`/admin/events/${id}`).then((r) => r.data),
  adminCancelInvoice: (id) => client.delete(`/admin/invoices/${id}`).then((r) => r.data),
  adminClearPreview: (unitId, meterType) => client.get(`/admin/units/${unitId}/readings/clear-preview`, { params: { meter_type: meterType } }).then((r) => r.data),
  adminClearReadings: (unitId, meterType, confirm) => client.post(`/admin/units/${unitId}/readings/clear`, { meter_type: meterType, confirm_unit_number: confirm }).then((r) => r.data),
  adminXeroStatus: () => client.get("/admin/xero/status").then((r) => r.data),
  adminXeroConnect: () => client.post("/admin/xero/connect").then((r) => r.data),
  adminXeroDisconnect: () => client.post("/admin/xero/disconnect").then((r) => r.data),
  adminUpdateOverdueSettings: (body) => client.put("/admin/overdue-settings", body).then((r) => r.data),
  // maintenance logs
  listMaintenance: (site) => client.get("/maintenance", { params: { site } }).then((r) => r.data),
  createMaintenance: (body) => client.post("/maintenance", body).then((r) => r.data),
  updateMaintenance: (id, body) => client.patch(`/maintenance/${id}`, body).then((r) => r.data),
  deleteMaintenance: (id) => client.delete(`/maintenance/${id}`).then((r) => r.data),
  publicMaintenanceSite: (site) => client.get("/public/maintenance/site", { params: { site } }).then((r) => r.data),
  submitPublicMaintenance: (body) => client.post("/public/maintenance", body).then((r) => r.data),
  // assets & per-asset QR logging
  listAssets: (site) => client.get("/assets", { params: { site } }).then((r) => r.data),
  createAsset: (body) => client.post("/assets", body).then((r) => r.data),
  updateAsset: (id, body) => client.patch(`/assets/${id}`, body).then((r) => r.data),
  deleteAsset: (id) => client.delete(`/assets/${id}`).then((r) => r.data),
  publicAsset: (token) => client.get(`/public/asset/${token}`).then((r) => r.data),
  submitAssetLog: (token, body) => client.post(`/public/asset/${token}/log`, body).then((r) => r.data),
  submitWebsiteEnquiry: (body) => client.post("/public/website-enquiry", body).then((r) => r.data),
  // blocked times
  getBlockedTimes: () => client.get("/blocked-times").then((r) => r.data),
  addBlockedTime: (body) => client.post("/blocked-times", body).then((r) => r.data),
  deleteBlockedTime: (id) => client.delete(`/blocked-times/${id}`).then((r) => r.data),
};